import { uiError, t, ui } from '../i18n';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import {
  AccountTree as IntegrationIcon,
  Dashboard as DashboardIcon,
  Public as PublicIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
  Security as SecurityIcon,
  Storage as StorageIcon,
  Today as TodayIcon
} from '@mui/icons-material';
import { useIntegrationLedgerStream } from '../hooks/useIntegrationLedgerStream';
import { getWebappApiUrl, webappFetch } from '../utils/apiConfig';
import {
  IntegrationLedgerTransaction,
  blockNumberValue,
  formatBytes,
  formatTime,
  koreaDateKey,
  liveBusinessId,
  liveOperation,
  livePayloadSizeBytes,
  shortBusinessId,
  shortHash,
  sourceOrgOf,
  targetOrgOf,
  todayKstRows
} from '../domain/integrationLedger';
import {
  DashboardMetricCard,
  IndexStatusChip,
  IntegrationTransactionDetailDialog,
  LedgerStatusChip
} from '../components/integration/IntegrationLedgerDialogs';

const CAPTURE_REPLAY_PARAM = '20260522-morning';
const CAPTURE_BASELINE_CUTOFF_ISO = '2026-05-22T00:00:00.000Z';
const CAPTURE_DATE_KEY = '2026-05-22';
const CAPTURE_DASHBOARD_BLOCKS = ['333', '334', '335'];
const CAPTURE_BLOCK_EXPLORER_BLOCKS = ['336', '337', '338'];
const CAPTURE_TRANSACTION_BLOCKS = ['339', '340', '341'];
const CAPTURE_DASHBOARD_DEFAULT_ON = false;
const DASHBOARD_FROM_BLOCK = 0;
const DASHBOARD_LIMIT = 1000;
const DASHBOARD_RECENT_LIMIT = 10;

type DashboardSummaryData = {
  summary?: Record<string, any>;
  networkStats?: Record<string, any>;
  recentTransactions?: IntegrationLedgerTransaction[];
  emittedAt?: string;
};

type CaptureReplaySteps = {
  dashboard: number;
  transactions: number;
  blocks: number;
};

const EMPTY_CAPTURE_STEPS: CaptureReplaySteps = {
  dashboard: 0,
  transactions: 0,
  blocks: 0
};

function boundedCaptureStep(value: unknown, max: number) {
  const step = Number(value || 0);
  if (!Number.isFinite(step)) return 0;
  return Math.max(0, Math.min(step, max));
}

function readCaptureReplaySteps(data: any): CaptureReplaySteps {
  return {
    dashboard: boundedCaptureStep(data?.dashboard?.step, CAPTURE_DASHBOARD_BLOCKS.length),
    transactions: boundedCaptureStep(data?.transactions?.step, CAPTURE_TRANSACTION_BLOCKS.length),
    blocks: boundedCaptureStep(data?.blocks?.step, CAPTURE_BLOCK_EXPLORER_BLOCKS.length)
  };
}

function dashboardRecordType(tx: IntegrationLedgerTransaction) {
  const source = sourceOrgOf(tx);
  if (source === 'EINS') return t("eINS Dispatch Optimization");
  if (source === 'KAIST') return t("KAIST Yard Optimization");
  if (source === 'KULS') return t("KULS Business Record");
  if (tx.chaincode_name === '_lifecycle') return t("Chaincode Lifecycle");
  return tx.record_type || tx.function_name || '-';
}

function isBeforeCaptureBaseline(tx: IntegrationLedgerTransaction) {
  const timestamp = new Date(tx.block_created_at).getTime();
  return Number.isFinite(timestamp) && timestamp < new Date(CAPTURE_BASELINE_CUTOFF_ISO).getTime();
}

function sortLedgerRowsDescending(rows: IntegrationLedgerTransaction[]) {
  return rows.slice().sort((a, b) => (
    blockNumberValue(b.block_number) - blockNumberValue(a.block_number)
    || (b.transaction_index || 0) - (a.transaction_index || 0)
  ));
}

function TodayActivityValue({
  blocks,
  transactions
}: {
  blocks: number;
  transactions: number;
}) {
  return (
    <Box
      component="span"
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 1.25,
        width: '100%',
        maxWidth: 190
      }}
    >
      {[
        ['Blocks', blocks],
        ['Tx', transactions]
      ].map(([label, value]) => (
        <Box
          component="span"
          key={label}
          sx={{
            display: 'block',
            minWidth: 0,
            pr: 1,
            '&:first-of-type': {
              borderRight: '1px solid rgba(255, 255, 255, 0.45)'
            }
          }}
        >
          <Typography component="span" sx={{ display: 'block', fontSize: 30, fontWeight: 900, lineHeight: 1 }}>
            {Number(value).toLocaleString()}
          </Typography>
          <Typography component="span" sx={{ display: 'block', fontSize: 11, fontWeight: 800, opacity: 0.82, mt: 0.4 }}>
            {ui(label)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function CompactFlowMetricValue({ value }: { value: string }) {
  return (
    <Typography
      component="span"
      sx={{
        display: 'block',
        fontSize: { xs: 27, sm: 29, lg: 30 },
        fontWeight: 900,
        lineHeight: 1.05,
        whiteSpace: 'nowrap',
        letterSpacing: 0
      }}
    >
      {value}
    </Typography>
  );
}

const Dashboard: React.FC = () => {
  const [selectedTx, setSelectedTx] = useState<IntegrationLedgerTransaction | null>(null);
  const [captureReplaySteps, setCaptureReplaySteps] = useState<CaptureReplaySteps>(EMPTY_CAPTURE_STEPS);
  const [captureHighlightedBlocks, setCaptureHighlightedBlocks] = useState<Set<string>>(new Set());
  const [summaryData, setSummaryData] = useState<DashboardSummaryData | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [summaryNewTxIds, setSummaryNewTxIds] = useState<Set<string>>(new Set());
  const replayTimersRef = useRef<number[]>([]);
  const lastCaptureRevisionRef = useRef<number | null>(null);
  const knownSummaryTxIdsRef = useRef<Set<string>>(new Set());
  const initializedSummaryRef = useRef(false);
  const summaryHighlightTimersRef = useRef<number[]>([]);

  const captureReplayMode = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('liveDashboard') === '1') return false;
    return CAPTURE_DASHBOARD_DEFAULT_ON || params.get('captureReplay') === CAPTURE_REPLAY_PARAM;
  }, []);

  const ledger = useIntegrationLedgerStream({
    fromBlock: DASHBOARD_FROM_BLOCK,
    limit: DASHBOARD_LIMIT,
    enabled: captureReplayMode
  });

  const clearReplayTimers = useCallback(() => {
    replayTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    replayTimersRef.current = [];
  }, []);

  useEffect(() => () => {
    clearReplayTimers();
    summaryHighlightTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    summaryHighlightTimersRef.current = [];
  }, [clearReplayTimers]);

  const hydrateSummary = useCallback((data: DashboardSummaryData) => {
    const rows = data.recentTransactions || [];
    const nextTxIds = new Set(rows.map((tx) => tx.transaction_id).filter(Boolean) as string[]);

    if (initializedSummaryRef.current) {
      const newIds = Array.from(nextTxIds).filter((id) => !knownSummaryTxIdsRef.current.has(id));
      if (newIds.length) {
        setSummaryNewTxIds((previous) => {
          const merged = new Set(previous);
          newIds.forEach((id) => merged.add(id));
          return merged;
        });
        const timer = window.setTimeout(() => {
          setSummaryNewTxIds((previous) => {
            const next = new Set(previous);
            newIds.forEach((id) => next.delete(id));
            return next;
          });
        }, 4000);
        summaryHighlightTimersRef.current.push(timer);
      }
    }

    knownSummaryTxIdsRef.current = nextTxIds;
    initializedSummaryRef.current = true;
    setSummaryData(data);
  }, []);

  const loadSummary = useCallback(async ({ initial = false }: { initial?: boolean } = {}) => {
    if (captureReplayMode) return;
    try {
      if (initial) setSummaryLoading(true);
      setSummaryError(null);
      const response = await webappFetch(`${getWebappApiUrl()}/api/blockchain/integration-prototype/summary?fromBlock=0&recentLimit=${DASHBOARD_RECENT_LIMIT}`);
      const body = await response.json();
      if (!response.ok || !body.success) {
        throw new Error(body.message || body.error || `HTTP ${response.status}`);
      }
      hydrateSummary(body.data || {});
    } catch (error) {
      setSummaryError(error instanceof Error ? error.message : 'Unknown error');
    } finally {
      if (initial) setSummaryLoading(false);
    }
  }, [captureReplayMode, hydrateSummary]);

  useEffect(() => {
    if (captureReplayMode) return undefined;
    loadSummary({ initial: true });
    const timer = window.setInterval(() => loadSummary(), 2000);
    return () => window.clearInterval(timer);
  }, [captureReplayMode, loadSummary]);

  const displayedBlockNumbers = useMemo(() => {
    if (!captureReplayMode) return null;
    return new Set([
      ...CAPTURE_DASHBOARD_BLOCKS.slice(0, captureReplaySteps.dashboard),
      ...CAPTURE_TRANSACTION_BLOCKS.slice(0, captureReplaySteps.transactions),
      ...CAPTURE_BLOCK_EXPLORER_BLOCKS.slice(0, captureReplaySteps.blocks)
    ]);
  }, [captureReplayMode, captureReplaySteps]);

  const displayTransactions = useMemo(() => {
    if (!captureReplayMode) return sortLedgerRowsDescending(summaryData?.recentTransactions || []);
    const rows = (!captureReplayMode || !displayedBlockNumbers)
      ? ledger.transactions
      : ledger.transactions.filter((tx) => (
        isBeforeCaptureBaseline(tx) || displayedBlockNumbers.has(String(tx.block_number))
      ));
    return sortLedgerRowsDescending(rows);
  }, [captureReplayMode, displayedBlockNumbers, ledger.transactions, summaryData?.recentTransactions]);

  const displayBusinessRows = useMemo(
    () => displayTransactions.filter((tx) => tx.chaincode_name !== '_lifecycle'),
    [displayTransactions]
  );

  const displayV1Rows = displayBusinessRows.filter((tx) => tx.function_name === 'RecordIntegrationResult');
  const displayLifecycleRows = displayTransactions.filter((tx) => tx.chaincode_name === '_lifecycle');
  const summary = summaryData?.summary || {};
  const summaryNetworkStats = summaryData?.networkStats || {};
  const todayRows = useMemo(() => (
    captureReplayMode
      ? displayBusinessRows.filter((tx) => koreaDateKey(tx.block_created_at) === CAPTURE_DATE_KEY)
      : todayKstRows(displayBusinessRows)
  ), [captureReplayMode, displayBusinessRows]);
  const hashRefRows = displayV1Rows.filter((tx) => Boolean(tx.payload_hash && tx.payload_ref));
  const largePayloadRows = displayV1Rows.filter((tx) => (livePayloadSizeBytes(tx) || 0) >= 1024 * 1024);
  const latestRecord = displayBusinessRows[0] || null;
  const latestBlock = displayTransactions[0] || null;
  const kaistToKulsRows = displayBusinessRows.filter((tx) => sourceOrgOf(tx) === 'KAIST' && targetOrgOf(tx) === 'KULS');
  const einsToKulsRows = displayBusinessRows.filter((tx) => sourceOrgOf(tx) === 'EINS' && targetOrgOf(tx) === 'KULS');
  const kulsSourceRows = displayBusinessRows.filter((tx) => sourceOrgOf(tx) === 'KULS');
  const displayLatestBlockNumber = captureReplayMode
    ? Math.max(0, ...displayTransactions.map((tx) => blockNumberValue(tx.block_number)))
    : Number(summaryNetworkStats.latest_block_number || summaryNetworkStats.latestBlockNumber || summary.latest_indexed_block || blockNumberValue(latestBlock?.block_number));
  const displayIndexedBlockCount = captureReplayMode
    ? new Set(displayTransactions.map((tx) => tx.block_number).filter(Boolean)).size
    : Number(summaryNetworkStats.total_blocks || summaryNetworkStats.totalBlocks || (displayLatestBlockNumber >= 0 ? displayLatestBlockNumber + 1 : 0));
  const displayTotalTransactions = captureReplayMode
    ? displayTransactions.length
    : Number(summaryNetworkStats.total_transactions || summaryNetworkStats.totalTransactions || summary.displayed_records || displayTransactions.length);
  const displayTodayBlockCount = captureReplayMode
    ? new Set(displayTransactions
      .filter((tx) => koreaDateKey(tx.block_created_at) === CAPTURE_DATE_KEY)
      .map((tx) => tx.block_number)
      .filter(Boolean)).size
    : Number(summary.today_blocks || 0);
  const displayTodayTransactionCount = captureReplayMode
    ? displayTransactions.filter((tx) => koreaDateKey(tx.block_created_at) === CAPTURE_DATE_KEY).length
    : Number(summary.today_transactions || 0);
  const hasHighlight = captureReplayMode
    ? ledger.newTransactionIds.size > 0 || ledger.newBlockNumbers.size > 0 || captureHighlightedBlocks.size > 0
    : summaryNewTxIds.size > 0;

  const recentRows = displayBusinessRows.slice(0, 10);
  const latestMetricRecord = latestRecord;
  const captureWindowRows = displayBusinessRows.filter((tx) => CAPTURE_DASHBOARD_BLOCKS.includes(String(tx.block_number)));
  const captureEinsRows = captureWindowRows.filter((tx) => sourceOrgOf(tx) === 'EINS');
  const captureKaistRows = captureWindowRows.filter((tx) => sourceOrgOf(tx) === 'KAIST');
  const captureDailyRows = captureEinsRows.filter((tx) => liveOperation(tx) === 'Daily_Opt');
  const captureEmergencyRows = captureEinsRows.filter((tx) => liveOperation(tx) === 'Emergency_Opt');
  const captureRetrievalRows = captureKaistRows.filter((tx) => liveOperation(tx) === 'retrieval');
  const captureAssignmentRows = captureKaistRows.filter((tx) => liveOperation(tx) === 'assignment');
  const captureRelocationRows = captureKaistRows.filter((tx) => liveOperation(tx) === 'relocation');

  const resetCaptureReplay = useCallback(() => {
    clearReplayTimers();
    setCaptureReplaySteps((current) => ({ ...current, dashboard: 0 }));
    setCaptureHighlightedBlocks(new Set());
  }, [clearReplayTimers]);

  const showDashboardCaptureStep = useCallback((step: number, animate = false) => {
    clearReplayTimers();
    const boundedStep = boundedCaptureStep(step, CAPTURE_DASHBOARD_BLOCKS.length);
    const blockNumber = CAPTURE_DASHBOARD_BLOCKS[boundedStep - 1];
    setCaptureReplaySteps((current) => ({ ...current, dashboard: boundedStep }));

    if (!animate || !blockNumber) return;

    setCaptureHighlightedBlocks(new Set([blockNumber]));
    const clearTimer = window.setTimeout(() => {
      setCaptureHighlightedBlocks((current) => {
        const next = new Set(current);
        next.delete(blockNumber);
        return next;
      });
    }, 4000);
    replayTimersRef.current.push(clearTimer);
  }, [clearReplayTimers]);

  useEffect(() => {
    if (!captureReplayMode) return undefined;

    let closed = false;
    const checkCaptureTrigger = async () => {
      try {
        const response = await webappFetch(`${getWebappApiUrl()}/api/blockchain/integration-prototype/capture-replay/state`);
        const body = await response.json();
        const allState = body?.data;
        const state = allState?.dashboard;
        if (!state || closed) return;
        const nextSteps = readCaptureReplaySteps(allState);
        const revision = Number(state.revision || 0);

        if (lastCaptureRevisionRef.current === null) {
          lastCaptureRevisionRef.current = revision;
          setCaptureReplaySteps(nextSteps);
          return;
        }

        setCaptureReplaySteps(nextSteps);
        if (revision <= lastCaptureRevisionRef.current) return;

        lastCaptureRevisionRef.current = revision;
        if (state.action === 'start') {
          showDashboardCaptureStep(nextSteps.dashboard, true);
        } else if (state.action === 'reset') {
          resetCaptureReplay();
        }
      } catch {
        // Capture trigger polling is intentionally silent. Ledger data display
        // should not fail if the local trigger endpoint is unavailable.
      }
    };

    checkCaptureTrigger();
    const timer = window.setInterval(checkCaptureTrigger, 700);
    return () => {
      closed = true;
      window.clearInterval(timer);
    };
  }, [captureReplayMode, resetCaptureReplay, showDashboardCaptureStep]);

  return (
    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#1976d2', fontWeight: 'bold' }}>
          <DashboardIcon sx={{ fontSize: 34 }} />{t("Dashboard")}</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>{t("Monitor SOCFAI blockchain business records committed by the network and indexed in the WebApp.")}</Typography>
      </Box>

      {(captureReplayMode ? ledger.error : summaryError) && (
        <Alert severity="error" sx={{ mb: 2 }}>{uiError(captureReplayMode ? ledger.error : summaryError)}</Alert>
      )}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <DashboardMetricCard
            label={t("Total Blocks")}
            value={displayIndexedBlockCount.toLocaleString()}
            caption={Number.isFinite(displayLatestBlockNumber) ? t("Indexed blocks, #0 to #{0}", [displayLatestBlockNumber]) : t("Indexed blockchain blocks")}
            color="#1976d2"
            gradient="linear-gradient(135deg, #1976d2 0%, #1565c0 100%)"
            icon={<PublicIcon sx={{ fontSize: 48 }} />}
            highlight={hasHighlight}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <DashboardMetricCard
            label={t("Total Transactions")}
            value={displayTotalTransactions.toLocaleString()}
            caption={t("Indexed blockchain transactions")}
            color="#26a69a"
            gradient="linear-gradient(135deg, #26a69a 0%, #00897b 100%)"
            icon={<IntegrationIcon sx={{ fontSize: 48 }} />}
            highlight={displayBusinessRows.some((tx) => ledger.newTransactionIds.has(tx.transaction_id) || captureHighlightedBlocks.has(String(tx.block_number)))}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <DashboardMetricCard
            label={captureReplayMode ? t("Replay Activity") : t("Today's Activity")}
            value={<TodayActivityValue blocks={displayTodayBlockCount} transactions={displayTodayTransactionCount} />}
            caption={captureReplayMode ? t("Loaded blocks / transactions in replay") : t("KST blocks / transactions indexed today")}
            color="#ff9800"
            gradient="linear-gradient(135deg, #ff9800 0%, #f57c00 100%)"
            icon={<TodayIcon sx={{ fontSize: 48 }} />}
            highlight={todayRows.some((tx) => ledger.newTransactionIds.has(tx.transaction_id) || captureHighlightedBlocks.has(String(tx.block_number)))}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <DashboardMetricCard
            label={t("Latest Activity")}
            value={<CompactFlowMetricValue value={latestMetricRecord ? `${sourceOrgOf(latestMetricRecord)} → ${targetOrgOf(latestMetricRecord)}` : '-'} />}
            caption={latestMetricRecord ? t("{0} / Block #{1}", [liveOperation(latestMetricRecord), latestMetricRecord.block_number]) : t("No indexed record yet")}
            color="#7b1fa2"
            gradient="linear-gradient(135deg, #7b1fa2 0%, #6a1b9a 100%)"
            icon={<ScheduleIcon sx={{ fontSize: 48 }} />}
            highlight={hasHighlight}
          />
        </Grid>
      </Grid>

      {false && <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
            {captureReplayMode ? (
              <>
                <Typography variant="h6" fontWeight="bold" gutterBottom>{t("eINS Operations")}</Typography>
                <Stack spacing={1.25}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography>{t("Daily_Opt")}</Typography>
                    <Chip color="success" variant="outlined" label={`${captureDailyRows.length}`} />
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography>{t("Emergency_Opt")}</Typography>
                    <Chip color="warning" variant="outlined" label={`${captureEmergencyRows.length}`} />
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography>{t("Displayed eINS Records")}</Typography>
                    <Chip variant="outlined" label={`${captureEinsRows.length}`} />
                  </Stack>
                  <Typography variant="caption" color="text.secondary">{t("No eINS record is included in the selected May 22 window unless shown above.")}</Typography>
                </Stack>
              </>
            ) : (
              <>
                <Typography variant="h6" fontWeight="bold" gutterBottom>{t("Partner Activity")}</Typography>
                <Stack spacing={1.25}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography>{t("eINS → KULS Dispatch Results")}</Typography>
                    <Chip color="success" label={`${einsToKulsRows.length}`} />
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography>{t("KAIST → KULS Yard Results")}</Typography>
                    <Chip color="primary" label={`${kaistToKulsRows.length}`} />
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography>{t("KULS Indexed Records")}</Typography>
                    <Chip variant="outlined" label={`${kulsSourceRows.length}`} />
                  </Stack>
                  <Typography variant="caption" color="text.secondary">{t("The dashboard includes historical PostgreSQL index rows from Block #0 and uses the capture cutoff for the displayed state.")}</Typography>
                </Stack>
              </>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
            {captureReplayMode ? (
              <>
                <Typography variant="h6" fontWeight="bold" gutterBottom>{t("KAIST Operations")}</Typography>
                <Stack spacing={1.25}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography>{t("assignment")}</Typography>
                    <Chip color="success" label={`${captureAssignmentRows.length}`} />
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography>{t("retrieval")}</Typography>
                    <Chip color="primary" variant="outlined" label={`${captureRetrievalRows.length}`} />
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography>{t("relocation")}</Typography>
                    <Chip variant="outlined" label={`${captureRelocationRows.length}`} />
                  </Stack>
                  <Typography variant="caption" color="text.secondary">{t("KAIST counts follow the currently displayed selected records.")}</Typography>
                </Stack>
              </>
            ) : (
              <>
                <Typography variant="h6" fontWeight="bold" gutterBottom>{t("Evidence Summary")}</Typography>
                <Stack spacing={1.25}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography>{t("Payload Evidence Ready")}</Typography>
                    <Chip color="success" label={`${hashRefRows.length}`} />
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography>{t("Large Payload Records")}</Typography>
                    <Chip color={largePayloadRows.length ? 'warning' : 'default'} variant={largePayloadRows.length ? 'filled' : 'outlined'} label={`${largePayloadRows.length}`} />
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography>{t("Chaincode Lifecycle Events")}</Typography>
                    <Chip color="secondary" variant="outlined" label={`${displayLifecycleRows.length}`} />
                  </Stack>
                  <Typography variant="caption" color="text.secondary">{t("Detail views keep the technical evidence model separate from the operational summary.")}</Typography>
                </Stack>
              </>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
            <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="flex-start">
              <Box>
                <Typography variant="h6" fontWeight="bold" gutterBottom>{t("Sync Status")}</Typography>
                <Typography variant="body2" color="text.secondary">{t("This screen uses PostgreSQL index snapshots and SSE updates. Raw payloads are loaded only in detail views.")}</Typography>
              </Box>
              <SecurityIcon color="primary" />
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
              <Chip size="small" color="info" variant="outlined" label={t("PostgreSQL index snapshot")} />
              <Chip size="small" variant="outlined" label={t("{0} records displayed", [displayTransactions.length])} />
              {captureReplayMode && <Chip size="small" variant="outlined" label={t("{0}/{1} selected records", [captureWindowRows.length, CAPTURE_DASHBOARD_BLOCKS.length])} />}
              <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={() => ledger.reload()}>{t("Refresh")}</Button>
            </Stack>
          </Paper>
        </Grid>
      </Grid>}

      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={1.5} sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h6" fontWeight="bold">{t("Recent Business Records")}</Typography>
            <Typography variant="body2" color="text.secondary">{t("Latest committed records are shown first. Select a row to inspect the detailed ledger record.")}</Typography>
          </Box>
          <Chip icon={<StorageIcon />} color="primary" variant="outlined" label={t("{0} shown", [recentRows.length])} />
        </Stack>

        <TableContainer>
          <Table size="small" sx={{ minWidth: 1020 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell><strong>{t("Time")}</strong></TableCell>
                <TableCell><strong>{t("Block")}</strong></TableCell>
                <TableCell><strong>{t("Tx ID")}</strong></TableCell>
                <TableCell><strong>{t("Source → Target")}</strong></TableCell>
                <TableCell><strong>{t("Record Type")}</strong></TableCell>
                <TableCell><strong>{t("Business ID")}</strong></TableCell>
                <TableCell><strong>{t("Operation")}</strong></TableCell>
                <TableCell><strong>{t("Payload")}</strong></TableCell>
                <TableCell><strong>{t("Status")}</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(captureReplayMode ? ledger.loading : summaryLoading) ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                    <CircularProgress size={26} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{t("Syncing dashboard data.")}</Typography>
                  </TableCell>
                </TableRow>
              ) : recentRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 5 }}>{t("No partner records to display.")}</TableCell>
                </TableRow>
              ) : (
                recentRows.map((tx) => (
                  <TableRow
                    hover
                    key={tx.transaction_id}
                    onClick={() => setSelectedTx(tx)}
                    sx={{
                      cursor: 'pointer',
                      ...((captureReplayMode
                        ? ledger.newTransactionIds.has(tx.transaction_id) || captureHighlightedBlocks.has(String(tx.block_number))
                        : summaryNewTxIds.has(tx.transaction_id)
                      ) ? {
                        animation: 'adminDashboardNewRowFlash 4s ease-out',
                        '@keyframes adminDashboardNewRowFlash': {
                          '0%': { backgroundColor: 'rgba(46, 125, 50, 0.24)' },
                          '18%': { backgroundColor: 'rgba(46, 125, 50, 0.16)' },
                          '100%': { backgroundColor: 'transparent' }
                        }
                      } : {})
                    }}
                  >
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatTime(tx.block_created_at)}</TableCell>
                    <TableCell>#{tx.block_number}</TableCell>
                    <TableCell title={tx.transaction_id} sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                      {shortHash(tx.transaction_id, 10, 8)}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {sourceOrgOf(tx) || '-'} → {targetOrgOf(tx) || '-'}
                    </TableCell>
                    <TableCell>{dashboardRecordType(tx)}</TableCell>
                    <TableCell title={liveBusinessId(tx)} sx={{ fontFamily: 'monospace', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {shortBusinessId(liveBusinessId(tx))}
                    </TableCell>
                    <TableCell>{liveOperation(tx)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatBytes(livePayloadSizeBytes(tx))}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        <LedgerStatusChip tx={tx} />
                        <IndexStatusChip tx={tx} />
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <IntegrationTransactionDetailDialog tx={selectedTx} onClose={() => setSelectedTx(null)} />
    </Box>
  );
};

export default Dashboard;
